import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ContractHudProps } from '../../common/GameComponentContracts';
import { useArenaStore } from '../../../store/useArenaStore';
import { GameHudProps } from '../../../presentation/GamePresentationTypes';
import { SeatPosition } from '../../../state/ArenaTypes';

function clampVmin(minPx: number, vminVal: number, maxPx: number): string {
  return `clamp(${minPx}px, ${vminVal}vmin, ${maxPx}px)`;
}

export const SpadesTableHud: React.FC<GameHudProps> = ({
  tableColor = 'default',
  team1Bid = 0,
  team2Bid = 0,
  team1Books = 0,
  team2Books = 0,
  team1Bags = 0,
  team2Bags = 0,
  spadesBroken = false,
  isPlayPhase = false,
  booksToSetData = null,
  gameState,
  config: propConfig,
}) => {
  const storeConfig = useArenaStore((s) => s.gameConfig);
  const activeConfig = propConfig ?? storeConfig;
  const showBags = activeConfig?.ui?.showBagsCounter ?? true;

  // Authoritatively derive bids from gameState.bids if booksToSetData or props were missing or 0
  const { t1BidFromBids, t2BidFromBids } = React.useMemo(() => {
    const rawBids = gameState?.bids || [];
    let b1 = 0;
    let b2 = 0;
    for (const b of rawBids) {
      if (!b || b.isPass || b.bid === undefined || b.bid === null) continue;
      const s = Number(b.seat);
      if (s === 0 || s === 2) {
        b1 += Number(b.bid) || 0;
      } else if (s === 1 || s === 3) {
        b2 += Number(b.bid) || 0;
      }
    }
    return { t1BidFromBids: b1, t2BidFromBids: b2 };
  }, [gameState?.bids]);

  const effectiveT1Bid =
    booksToSetData?.team1?.bid ??
    booksToSetData?.teams?.[1]?.target ??
    booksToSetData?.team1?.target ??
    (team1Bid > 0 ? team1Bid : t1BidFromBids);

  const effectiveT2Bid =
    booksToSetData?.team2?.bid ??
    booksToSetData?.teams?.[2]?.target ??
    booksToSetData?.team2?.target ??
    (team2Bid > 0 ? team2Bid : t2BidFromBids);

  const effectiveT1Books =
    booksToSetData?.team1?.booksWon ??
    booksToSetData?.teams?.[1]?.current ??
    gameState?.team1Books ??
    team1Books;

  const effectiveT2Books =
    booksToSetData?.team2?.booksWon ??
    booksToSetData?.teams?.[2]?.current ??
    gameState?.team2Books ??
    team2Books;

  const effectiveT1Bags = gameState?.team1Bags ?? team1Bags;
  const effectiveT2Bags = gameState?.team2Bags ?? team2Bags;
  const effectiveSpadesBroken = Boolean(gameState?.spadesBroken ?? spadesBroken);

  const colorStyles = React.useMemo(() => {
    switch (tableColor) {
      case 'blue':
        return {
          border: 'border-sky-200/40',
          separator: 'border-sky-200/30',
          primaryText: 'text-sky-100',
          gameTitle: 'text-sky-300',
        };
      case 'red':
        return {
          border: 'border-rose-200/40',
          separator: 'border-rose-200/30',
          primaryText: 'text-rose-100',
          gameTitle: 'text-rose-300',
        };
      case 'pink':
        return {
          border: 'border-pink-200/40',
          separator: 'border-pink-200/30',
          primaryText: 'text-pink-100',
          gameTitle: 'text-pink-300',
        };
      case 'gold':
        return {
          border: 'border-amber-200/40',
          separator: 'border-amber-200/30',
          primaryText: 'text-amber-100',
          gameTitle: 'text-amber-300',
        };
      case 'default':
      default:
        return {
          border: 'border-emerald-200/40',
          separator: 'border-emerald-200/30',
          primaryText: 'text-emerald-100',
          gameTitle: 'text-emerald-300',
        };
    }
  }, [tableColor]);

  const fontSize = {
    base: clampVmin(11, 2.8, 20),
    bid: clampVmin(12, 3.2, 22),
    books: clampVmin(10, 2.4, 16.5),
  } as const;

  // Stays mounted during play phase, trick review, or hand complete
  const isPlay = isPlayPhase || gameState?.phase === 'PLAYING' || gameState?.phase === 'TRICK_END' || gameState?.phase === 'TRICK_RESOLVED' || gameState?.phase === 'HAND_END';
  if (!isPlay || !showBags) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: -12, y: -6, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
        exit={{ opacity: 0, x: -12, y: -6, scale: 0.95 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed top-2.5 left-4 sm:top-3.5 sm:left-5 z-[60] pointer-events-none select-none"
      >
        <div
          data-testid="spades-contract-hud"
          className={`relative h-[38px] sm:h-[42px] overflow-hidden bg-gradient-to-b from-[#051c14]/95 via-[#03140e]/98 to-[#010a07]/98 backdrop-blur-xl rounded-xl sm:rounded-2xl px-2.5 sm:px-3 py-1 flex items-center gap-2 sm:gap-2.5 shadow-[0_12px_36px_rgba(0,0,0,0.85),0_4px_16px_rgba(0,0,0,0.6),inset_0_1px_0_0_rgba(255,255,255,0.2)] border ${colorStyles.border}`}
        >
          {/* Luxury glass top edge inner highlight reflection */}
          <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-emerald-300/40 to-transparent pointer-events-none" />

          {/* Hidden/Accessibility game header */}
          <span data-testid="spades-game-header" className="sr-only">
            SPADES
          </span>

          {/* Section 1: Spades Broken Status */}
          <div className="flex items-center gap-1.5 leading-none">
            {effectiveSpadesBroken ? (
              <div
                data-testid="spades-broken-indicator"
                title="Spades Broken (Spades may be led)"
                className="flex items-center gap-1 leading-none"
              >
                <span className="text-[10px] sm:text-[11px] font-black font-mono uppercase tracking-wider text-purple-400 drop-shadow-[0_0_6px_rgba(192,132,252,0.6)]">
                  ♠ BROKEN
                </span>
              </div>
            ) : (
              <div
                data-testid="spades-not-broken-indicator"
                title="Spades Not Broken"
                className="flex items-center gap-1 leading-none"
              >
                <span className="text-[10px] sm:text-[11px] font-black font-mono uppercase tracking-wider text-emerald-300/90">
                  ♠ NOT BROKEN
                </span>
              </div>
            )}
          </div>

          {/* Vertical Separator 1 */}
          <div
            data-testid="spades-hud-separator"
            className={`w-[1.5px] self-stretch bg-gradient-to-b from-emerald-500/10 via-emerald-300/60 to-emerald-500/10 my-0.5 rounded-full shadow-[0_0_4px_rgba(16,185,129,0.3)] ${colorStyles.separator}`}
          />

          {/* Section 2: Spades Target Contracts */}
          <div className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap text-[10.5px] sm:text-[11.5px] font-mono font-black">
            <span className="text-blue-400 drop-shadow-sm">
              T1: {effectiveT1Books}/{effectiveT1Bid}
            </span>
            <span className="text-slate-400/80 font-sans text-[9px] sm:text-[10px]">VS</span>
            <span className="text-red-400 drop-shadow-sm">
              T2: {effectiveT2Books}/{effectiveT2Bid}
            </span>
          </div>

          {/* Section 3: Bags (if showBags) */}
          {showBags && (
            <>
              {/* Vertical Separator 2 */}
              <div className={`w-[1.5px] self-stretch bg-gradient-to-b from-emerald-500/10 via-emerald-300/60 to-emerald-500/10 my-0.5 rounded-full shadow-[0_0_4px_rgba(16,185,129,0.3)] ${colorStyles.separator}`} />

              <div
                data-testid="spades-hud-details"
                className={`flex items-center gap-1 text-[9.5px] sm:text-[10.5px] font-black font-mono uppercase tracking-wider ${colorStyles.primaryText} leading-none`}
              >
                <span>
                  BAGS: {effectiveT1Bags} / {effectiveT2Bags}
                </span>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
